import { PROJECT_STATUSES, type ProjectStatus } from "@zplanem/responses"
import type React from "react"
import { useEffect, useRef, useState } from "react"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"
import { ProjectStatusBadge, statusBadgeClass } from "./project-status-badge"

const ChevronIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.75"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-3 h-3"
		aria-hidden="true"
	>
		<path d="m4 6 4 4 4-4" />
	</svg>
)

const CheckIcon: React.FC = () => (
	<svg
		viewBox="0 0 16 16"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
		className="w-3.5 h-3.5"
		aria-hidden="true"
	>
		<path d="m3 8 3.5 3.5L13 5" />
	</svg>
)

/**
 * The project status pill (ZPL-126). When the caller can change status it is
 * a dropdown trigger — the pill carries a chevron and opens a menu of
 * statuses (the old Details `<select>` is gone); otherwise it renders the
 * plain read-only badge. The `accepted` status stays owner-only.
 */
export function ProjectStatusMenu({
	status,
	canSetStatus,
	isOwner,
	pending,
	onSelect,
}: {
	status: ProjectStatus
	canSetStatus: boolean
	isOwner: boolean
	pending: boolean
	onSelect: (status: ProjectStatus) => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const [open, setOpen] = useState(false)
	const wrapperRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const handleDown = (e: MouseEvent) => {
			if (
				wrapperRef.current &&
				!wrapperRef.current.contains(e.target as Node)
			) {
				setOpen(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(false)
		}
		window.addEventListener("mousedown", handleDown)
		window.addEventListener("keydown", handleKey)
		return () => {
			window.removeEventListener("mousedown", handleDown)
			window.removeEventListener("keydown", handleKey)
		}
	}, [open])

	if (!canSetStatus) return <ProjectStatusBadge status={status} />

	return (
		<div ref={wrapperRef} className="relative">
			<button
				type="button"
				onClick={() => setOpen((o) => !o)}
				disabled={pending}
				aria-haspopup="menu"
				aria-expanded={open}
				aria-label={`${t.projects.detail.statusLabel}: ${t.projects.statuses[status]}`}
				className={`inline-flex items-center gap-1 rounded-sm border border-current/25 px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 disabled:opacity-50 ${statusBadgeClass(status)}`}
			>
				{t.projects.statuses[status]}
				<ChevronIcon />
			</button>
			{open ? (
				<div
					role="menu"
					className="absolute left-0 mt-2 w-56 rounded-xl border border-border bg-popover text-popover-foreground shadow-xl z-50 py-1"
				>
					{PROJECT_STATUSES.map((s) => {
						const disabled = s === "accepted" && !isOwner
						return (
							<button
								key={s}
								type="button"
								role="menuitemradio"
								aria-checked={s === status}
								disabled={disabled}
								title={
									disabled ? t.projects.detail.acceptedOwnerOnlyHint : undefined
								}
								onClick={() => {
									setOpen(false)
									if (s !== status) onSelect(s)
								}}
								className="w-full flex items-center justify-between gap-2 text-left px-3 py-2 text-sm text-foreground/80 hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
							>
								<span>{t.projects.statuses[s]}</span>
								{s === status ? (
									<span className="text-[color:var(--ring)]">
										<CheckIcon />
									</span>
								) : null}
							</button>
						)
					})}
				</div>
			) : null}
		</div>
	)
}
